/**
 * Scroll-container helpers.
 *
 * The app shell is `h-screen ... overflow-x-hidden`. CSS computes `overflow-y`
 * to `auto` whenever `overflow-x` is not `visible`, so that element — not the
 * window — is what actually scrolls. Anything reading `window.scrollY` or
 * calling `window.scrollTo()` is therefore a silent no-op on this site, and
 * `scroll` events fire on that element rather than on `window` (scroll events
 * do not bubble from elements).
 *
 * These helpers resolve the real scroller once and work either way, so the same
 * code keeps working if the shell ever goes back to document scrolling.
 *
 * Note that `getBoundingClientRect()` is viewport-relative and correct
 * regardless of which element scrolls — prefer it for measuring positions.
 */

/**
 * Nearest scrollable ancestor of `el`, or `null` when the document itself is
 * the scroller.
 */
export function findScroller(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const overflowY = getComputedStyle(node).overflowY;
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/** Current scroll offset of `scroller`, falling back to the window. */
export function scrollTopOf(scroller: HTMLElement | null): number {
  return scroller ? scroller.scrollTop : window.scrollY;
}

/** Scroll `scroller` by `delta` pixels, instantly. */
export function scrollByPx(scroller: HTMLElement | null, delta: number): void {
  if (scroller) {
    scroller.scrollTop += delta;
  } else {
    window.scrollTo({ top: window.scrollY + delta, behavior: 'auto' });
  }
}

/** Attach a passive scroll listener to whichever thing actually scrolls. */
export function onScroll(scroller: HTMLElement | null, fn: () => void): () => void {
  const targets: (HTMLElement | Window)[] = scroller ? [scroller, window] : [window];
  for (const t of targets) t.addEventListener('scroll', fn, { passive: true });
  window.addEventListener('resize', fn, { passive: true });
  return () => {
    for (const t of targets) t.removeEventListener('scroll', fn);
    window.removeEventListener('resize', fn);
  };
}
